'use client';

import supportContact from '@/lib/constants/support-contact.json';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Phone, Mail, MapPin, Clock, MessageSquare, CheckCircle, Headphones, Home } from 'lucide-react';
import { useSiteConfig, useContactInfo } from '@/context/SiteConfigContext';

export default function ContactPage() {
  const siteConfig = useSiteConfig();
  const contactInfo = useContactInfo();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  // Honeypot — hidden from humans (off-screen + aria-hidden + tabIndex -1).
  // Bots that fill every field submit a non-empty value and are rejected
  // server-side. Stays '' for real users.
  const [honeypot, setHoneypot] = useState('');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Time-to-submit: when the form became usable. Sent as elapsed ms (client
  // clock only) so the server can reject instant, scripted submissions.
  const formStartRef = useRef(0);
  useEffect(() => { formStartRef.current = Date.now(); }, []);
  // Carry the customer's shortlist into the existing consented contact flow.
  // These remain editable text fields, never HTML or automatic submissions.
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const subject = query.get('subject')?.slice(0, 180) || '';
    const message = query.get('message')?.slice(0, 3000) || '';
    if (subject || message) setFormData(current => ({...current, subject: current.subject || subject, message: current.message || message}));
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Cloudflare Turnstile — mirrors /register and /consultation.
  useEffect(() => {
    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
    if (!siteKey) {
      setTurnstileReady(true);
      return;
    }

    // Let the form submit if Turnstile fails to load; the server still applies
    // the honeypot, validation and rate limit.
    const fallbackTimer = setTimeout(() => setTurnstileReady(true), 5000);

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onContactTurnstileLoad';
    script.async = true;

    (window as any).onContactTurnstileLoad = () => {
      if (turnstileRef.current && (window as any).turnstile) {
        widgetIdRef.current = (window as any).turnstile.render(turnstileRef.current, {
          sitekey: siteKey,
          callback: (token: string) => {
            setTurnstileToken(token);
            setTurnstileReady(true);
            clearTimeout(fallbackTimer);
          },
          'expired-callback': () => setTurnstileToken(''),
        });
      }
    };

    document.head.appendChild(script);
    return () => {
      clearTimeout(fallbackTimer);
      script.remove();
      delete (window as any).onContactTurnstileLoad;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Server is fail-closed when the secret is configured, so catch a missing
    // token here rather than firing a request that's guaranteed to 403.
    if (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Please complete the 'I'm human' verification. If you don't see it, disable your ad-blocker and refresh.");
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/v2/commerce/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          turnstileToken,
          company_website: honeypot, // honeypot — always '' for real users
          formElapsedMs: formStartRef.current ? Date.now() - formStartRef.current : undefined,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
      } else {
        setError(result.error || 'Failed to submit. Please try again.');
        // Tokens are single-use — reset so the customer can retry.
        setTurnstileToken('');
        if (widgetIdRef.current && (window as any).turnstile) {
          (window as any).turnstile.reset(widgetIdRef.current);
        }
      }
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setError('An error occurred. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inquiryTypes = [
    'General Information',
    'Schedule Consultation',
    'Order Status',
    'Warranty Claim',
    'Installation Support',
    'Product Question',
    'Billing Question',
    'Other'
  ];

  // The public contact address is the sales / general-contact email (DB:
  // contact_email → email.sales, e.g. sales@smartblindspro.com), falling back to
  // support/info if a tenant only set one. Always DB-driven — never hardcode it.
  const supportEmail =
    contactInfo.email?.sales ||
    (contactInfo.email as any)?.contact ||
    contactInfo.email?.support ||
    contactInfo.email?.info ||
    '';

  const openChatWidget = () => {
    // Find LiveChatWidget's launcher button and click it.
    // The widget sits at fixed bottom-right with aria-label="Open live chat".
    if (typeof window === 'undefined') return;
    const launcher = document.querySelector<HTMLButtonElement>('button[aria-label="Open live chat"]');
    if (launcher) {
      launcher.click();
      launcher.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const contactMethods = [
    {
      icon: <Phone className="h-8 w-8 text-primary-red" />,
      title: 'Phone Support',
      description: 'Speak with our experts',
      contact: supportContact.phone,
      hours: `${contactInfo.hours?.weekday || ''}\n${contactInfo.hours?.saturday || ''}`,
      action: supportContact.phoneTel,
      actionText: 'Call Now',
      onClick: undefined as (() => void) | undefined,
    },
    {
      icon: <Mail className="h-8 w-8 text-green-600" />,
      title: 'Email Support',
      description: 'Send us your questions',
      contact: supportEmail,
      hours: 'Response within 24 hours',
      action: supportEmail ? `mailto:${supportEmail}` : undefined,
      actionText: 'Send Email',
      onClick: undefined as (() => void) | undefined,
    },
    {
      icon: <MessageSquare className="h-8 w-8 text-primary-red" />,
      title: 'Live Chat',
      description: 'Chat with us online',
      contact: 'Available on website',
      hours: 'Mon-Fri: 8AM-6PM PT',
      action: undefined,
      actionText: 'Start Chat',
      onClick: openChatWidget,
    }
  ];

  const locations = [
    {
      name: `${siteConfig.companyName} Headquarters`,
      address: contactInfo.address?.full || '',
      phone: contactInfo.phone,
      hours: `${contactInfo.hours?.weekday || ''}\n${contactInfo.hours?.saturday || ''}\nSun: Closed`
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-sb flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-heading-charcoal mb-4">Message Sent!</h2>
          <p className="text-sb-text-muted mb-6">
            Thank you for contacting us. We'll respond to your inquiry within 24 hours.
          </p>
          <div className="space-y-3">
            <Link
              href="/consultation"
              className="w-full bg-red-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors block"
            >
              Schedule Consultation
            </Link>
            <Link 
              href="/"
              className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-50 transition-colors block"
            >
              Return Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sb">
      {/* Hero Section */}
      <div className="bg-charcoal text-white">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-8 sm:py-10">
          <div className="text-center">
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
              Contact {siteConfig.companyName}
            </h1>
            <p className="text-sm sm:text-xl mb-6 sm:mb-8 text-blue-100 max-w-3xl mx-auto">
              We're here to help with all your window treatment needs. Get in touch with our expert team
              for consultations, support, or any questions about our products and services.
            </p>
            <div className="grid grid-cols-3 gap-3 sm:gap-6 mt-6 sm:mt-8">
              <div className="text-center">
                <Headphones className="h-8 w-8 sm:h-12 sm:w-12 text-blue-300 mx-auto mb-2 sm:mb-4" />
                <h3 className="text-sm sm:text-lg font-semibold mb-1 sm:mb-2">Expert Support</h3>
                <p className="text-blue-100 text-xs sm:text-base hidden sm:block">Knowledgeable team ready to assist you</p>
              </div>
              <div className="text-center">
                <Clock className="h-8 w-8 sm:h-12 sm:w-12 text-blue-300 mx-auto mb-2 sm:mb-4" />
                <h3 className="text-sm sm:text-lg font-semibold mb-1 sm:mb-2">Quick Response</h3>
                <p className="text-blue-100 text-xs sm:text-base hidden sm:block">Fast replies to all inquiries</p>
              </div>
              <div className="text-center">
                <Home className="h-8 w-8 sm:h-12 sm:w-12 text-blue-300 mx-auto mb-2 sm:mb-4" />
                <h3 className="text-sm sm:text-lg font-semibold mb-1 sm:mb-2">Local Service</h3>
                <p className="text-blue-100 text-xs sm:text-base hidden sm:block">Serving your area and beyond</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Methods */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-10 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-4xl font-bold text-heading-charcoal mb-2 sm:mb-4">
            Get In Touch
          </h2>
          <p className="text-sm sm:text-xl text-sb-text-muted max-w-3xl mx-auto">
            Choose the contact method that works best for you. Our team is standing by to help.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 mb-10 sm:mb-16">
          {contactMethods.map((method, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-4 sm:p-6 text-center">
              <div className="flex justify-center mb-3 sm:mb-4">
                {method.icon}
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-heading-charcoal mb-1 sm:mb-2">{method.title}</h3>
              <p className="text-sb-text-muted mb-3 sm:mb-4 text-sm">{method.description}</p>
              <div className="mb-3 sm:mb-4">
                <div className="font-semibold text-heading-charcoal text-sm sm:text-base">
                  {method.action?.startsWith('tel:')
                    ? <a href={method.action} data-track="contact-card-number" className="hover:text-primary-red">{method.contact}</a>
                    : method.contact}
                </div>
                <div className="text-xs sm:text-sm text-gray-500 whitespace-pre-line">{method.hours}</div>
              </div>
              {method.onClick ? (
                <button
                  type="button"
                  onClick={method.onClick}
                  className="bg-red-600 text-white px-4 sm:px-6 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors inline-block text-sm sm:text-base"
                >
                  {method.actionText}
                </button>
              ) : method.action ? (
                <a
                  href={method.action}
                  data-track={method.action?.startsWith('tel:') ? 'contact-card-call' : undefined}
                  className="bg-red-600 text-white px-4 sm:px-6 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors inline-block text-sm sm:text-base"
                >
                  {method.actionText}
                </a>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* Contact Form */}
      <div className="bg-white py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-3 sm:px-4">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-charcoal text-white p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Send Us a Message</h2>
              <p className="text-blue-100 text-sm sm:text-base">Fill out the form below and we'll get back to you soon.</p>
            </div>

            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Honeypot — invisible to humans, irresistible to form-filling bots.
                  Not display:none (some bots skip those); moved off-screen instead. */}
              <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
                <label htmlFor="company_website">Company website (leave blank)</label>
                <input maxLength={80}
                  id="company_website"
                  name="company_website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-firstName" className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input maxLength={60}
                    id="contact-firstName"
                    type="text"
                    name="firstName"
                    required
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="contact-lastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input maxLength={60}
                    id="contact-lastName"
                    type="text"
                    name="lastName"
                    required
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input maxLength={254}
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input maxLength={20}
                    id="contact-phone"
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    inputMode="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    pattern="[\d\s\-\(\)\+]{10,}"
                    title="Please enter a valid phone number (at least 10 digits)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Inquiry Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-inquiryType" className="block text-sm font-medium text-gray-700 mb-1">Inquiry Type</label>
                  <select
                    id="contact-inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select inquiry type</option>
                    {inquiryTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    required
                    maxLength={255}
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea maxLength={2000}
                  id="contact-message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us how we can help you..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Privacy Notice */}
              <p className="text-xs text-gray-500">
                By submitting this form, you agree to our <a href="/privacy" className="underline hover:text-gray-700">Privacy Policy</a>. We'll use your information to respond to your inquiry and may follow up with relevant communications.
              </p>

              {/* Cloudflare Turnstile anti-bot widget */}
              {process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
                <div ref={turnstileRef} className="flex justify-center" />
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !turnstileReady}
                className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-5 w-5 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Locations */}
      <div className="bg-sb py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-heading-charcoal mb-8 sm:mb-12">
            Visit Our Locations
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
            {locations.map((location, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
                <div className="flex items-start mb-3 sm:mb-4">
                  <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-primary-red mt-0.5 sm:mt-1 mr-2 sm:mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-heading-charcoal mb-1 sm:mb-2">{location.name}</h3>
                    <p className="text-sb-text-muted whitespace-pre-line mb-1 sm:mb-2 text-sm">{location.address}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div>
                    <div className="flex items-center mb-1 sm:mb-2">
                      <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 mr-1.5 sm:mr-2" />
                      {location.phone
                        ? <a href={`tel:+1${String(location.phone).replace(/[^0-9]/g, '').replace(/^1(?=\d{10}$)/, '')}`} data-track="contact-location" className="font-medium hover:text-primary-red">{location.phone}</a>
                        : null}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-start">
                      <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 mr-1.5 sm:mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sb-text-muted whitespace-pre-line">{location.hours}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-3 sm:px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-heading-charcoal mb-8 sm:mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-sb rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-heading-charcoal mb-1 sm:mb-2">What are your business hours?</h3>
              <p className="text-sb-text-muted text-sm sm:text-base">Our office is open Monday-Friday 8AM-6PM PT and Saturday 9AM-4PM PT, closed Sunday. Phone support is available during business hours.</p>
            </div>
            <div className="bg-sb rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-heading-charcoal mb-1 sm:mb-2">Do you offer free consultations?</h3>
              <p className="text-sb-text-muted text-sm sm:text-base">Yes! We offer free in-home consultations where our experts will measure your windows and provide personalized recommendations. Schedule yours today.</p>
            </div>
            <div className="bg-sb rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-heading-charcoal mb-1 sm:mb-2">What areas do you serve?</h3>
              <p className="text-sb-text-muted text-sm sm:text-base">Contact us to confirm service availability in your specific location. We serve a wide area and are always expanding.</p>
            </div>
            <div className="bg-sb rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-heading-charcoal mb-1 sm:mb-2">How quickly can you respond to my inquiry?</h3>
              <p className="text-sb-text-muted text-sm sm:text-base">We respond to phone calls immediately during business hours. Email inquiries are typically answered within 24 hours, often much sooner.</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-charcoal text-white py-10 sm:py-16">
        <div className="max-w-4xl mx-auto text-center px-3 sm:px-4">
          <h2 className="text-xl sm:text-3xl font-bold mb-3 sm:mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-sm sm:text-xl mb-5 sm:mb-8 text-blue-100">
            Contact us today for your free consultation and discover why {siteConfig.companyName} is the right choice.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              href="/consultation"
              className="bg-white text-primary-red px-5 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors text-sm sm:text-base"
            >
              Schedule Free Consultation
            </Link>
            <Link
              href="/samples"
              className="border-2 border-white text-white px-5 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-red transition-colors text-sm sm:text-base"
            >
              Design Yours Online
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
